import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';
import { PenLine, Eye, Loader2, Image as ImageIcon, CalendarCheck, Upload } from 'lucide-react';

const ADMIN_ROLES = ['tenant_admin', 'org_admin', 'admin', 'superadmin', 'super_admin'];

const EMPTY = {
  enabled: true, sign_off: 'Best regards,', full_name: '', title: '', company: '',
  phone: '', email: '', logo_url: '', book_a_call_url: '', website: '',
  linkedin: '', twitter: '', tagline: '',
};

const FIELDS = [
  { key: 'sign_off', label: 'Sign-off', placeholder: 'Best regards,' },
  { key: 'full_name', label: 'Full name', placeholder: 'Jane Doe' },
  { key: 'title', label: 'Job title', placeholder: 'Sales Lead' },
  { key: 'company', label: 'Company', placeholder: 'Acme Inc' },
  { key: 'phone', label: 'Phone', placeholder: '+1 555 000 1234' },
  { key: 'email', label: 'Email', placeholder: 'jane@acme.com' },
  { key: 'logo_url', label: 'Logo URL', placeholder: 'https://…/logo.png', icon: ImageIcon },
  { key: 'book_a_call_url', label: 'Book-a-call link', placeholder: 'https://cal.com/you', icon: CalendarCheck },
  { key: 'website', label: 'Website', placeholder: 'https://acme.com' },
  { key: 'linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/in/you' },
  { key: 'twitter', label: 'Twitter / X', placeholder: 'https://x.com/you' },
  { key: 'tagline', label: 'Tagline (optional)', placeholder: 'Helping teams close faster' },
];

function SignatureForm({ scope }) {
  // scope: 'mine' | 'default'
  const [cfg, setCfg] = useState(EMPTY);
  const [effective, setEffective] = useState({});
  const [preview, setPreview] = useState({ plain: '', html: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const debounceRef = useRef(null);
  const logoInputRef = useRef(null);

  const getUrl = scope === 'default'
    ? '/api/tenant/integrations/mail/signature/default'
    : '/api/tenant/integrations/mail/signature';
  const putUrl = getUrl;

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(getUrl);
        const data = res.data?.data || {};
        if (!active) return;
        setCfg({ ...EMPTY, ...(data.signature || {}) });
        setEffective(data.effective || data.signature || {});
        if (data.preview) setPreview(data.preview);
      } catch (e) {
        if (active) toast.error('Could not load signature');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [scope]);

  const refreshPreview = useCallback((next) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await api.post('/api/tenant/integrations/mail/signature/preview', next);
        setPreview(res.data?.data?.preview || { plain: '', html: '' });
      } catch (_e) { /* preview is best-effort */ }
    }, 350);
  }, []);

  const update = (key, value) => {
    const next = { ...cfg, [key]: value };
    setCfg(next);
    refreshPreview(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(putUrl, cfg);
      toast.success(scope === 'default' ? 'Workspace signature saved' : 'Your signature saved');
    } catch (e) {
      toast.error('Failed to save signature');
    } finally {
      setSaving(false);
    }
  };

  const onLogoFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!['image/png', 'image/jpeg', 'image/webp', 'image/gif'].includes(f.type)) {
      toast.error('Logo must be a PNG, JPG, WEBP or GIF.');
      return;
    }
    if (f.size > 2 * 1024 * 1024) {
      toast.error('Logo is too large (max 2 MB).');
      return;
    }
    setUploadingLogo(true);
    try {
      const form = new FormData();
      form.append('file', f);
      const res = await api.post('/api/tenant/integrations/mail/signature/logo', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data?.data?.url;
      if (url) {
        update('logo_url', url);
        toast.success('Logo uploaded');
      }
    } catch (e) {
      toast.error('Logo upload failed');
    } finally {
      setUploadingLogo(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Form */}
      <div className="space-y-3" data-testid={`signature-form-${scope}`}>
        <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 px-3 py-2">
          <div>
            <p className="text-sm font-medium">
              {scope === 'default' ? 'Use workspace signature' : 'Use my signature'}
            </p>
            <p className="text-xs text-muted-foreground">
              {scope === 'default'
                ? 'Fallback for teammates who haven’t set their own.'
                : 'When off, emails fall back to the workspace default or an auto signature.'}
            </p>
          </div>
          <Switch
            checked={!!cfg.enabled}
            onCheckedChange={(v) => update('enabled', v)}
            data-testid={`signature-enabled-${scope}`}
          />
        </div>

        {FIELDS.map(({ key, label, placeholder, icon: Icon }) => (
          <div key={key} className="space-y-1">
            <Label htmlFor={`sig-${scope}-${key}`} className="text-xs flex items-center gap-1.5">
              {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground" />}{label}
            </Label>
            {key === 'logo_url' ? (
              <div className="flex gap-2">
                <Input
                  id={`sig-${scope}-${key}`}
                  value={cfg[key] || ''}
                  placeholder={effective[key] || placeholder}
                  onChange={(e) => update(key, e.target.value)}
                  data-testid={`signature-input-${scope}-${key}`}
                />
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  ref={logoInputRef}
                  onChange={onLogoFile}
                  className="hidden"
                  data-testid={`signature-logo-file-${scope}`}
                />
                <Button
                  type="button" variant="outline" size="sm" className="shrink-0"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={uploadingLogo}
                  data-testid={`signature-logo-upload-${scope}`}
                >
                  {uploadingLogo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  <span className="ml-1">Upload</span>
                </Button>
              </div>
            ) : (
              <Input
                id={`sig-${scope}-${key}`}
                value={cfg[key] || ''}
                placeholder={effective[key] || placeholder}
                onChange={(e) => update(key, e.target.value)}
                data-testid={`signature-input-${scope}-${key}`}
              />
            )}
          </div>
        ))}
      </div>

      {/* Live preview */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Eye className="h-4 w-4" /> Live preview
        </div>
        <div
          className="rounded-lg border border-border bg-white p-4 min-h-[180px] overflow-auto"
          data-testid={`signature-preview-html-${scope}`}
        >
          {preview.html
            ? <div dangerouslySetInnerHTML={{ __html: preview.html }} />
            : <p className="text-xs text-muted-foreground">Fill in a name or company to see your signature.</p>}
        </div>
        <div className="rounded-lg border border-dashed border-border bg-muted/20 p-3">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Plain-text version</p>
          <pre className="text-xs whitespace-pre-wrap font-sans text-foreground" data-testid={`signature-preview-plain-${scope}`}>
            {preview.plain || '—'}
          </pre>
        </div>
        <Button onClick={save} disabled={saving} className="w-full" data-testid={`signature-save-${scope}`}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <PenLine className="h-4 w-4 mr-2" />}
          {scope === 'default' ? 'Save workspace signature' : 'Save my signature'}
        </Button>
      </div>
    </div>
  );
}

export default function SignatureEditor({ open, onOpenChange }) {
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="signature-editor-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PenLine className="h-5 w-5 text-primary" /> Email signature
          </DialogTitle>
          <DialogDescription>
            Added automatically to every AI & cadence email. Replaces the generic auto sign-off.
          </DialogDescription>
        </DialogHeader>

        {isAdmin ? (
          <Tabs defaultValue="mine" className="mt-2">
            <TabsList className="grid grid-cols-2 w-full max-w-sm">
              <TabsTrigger value="mine" data-testid="signature-tab-mine">My signature</TabsTrigger>
              <TabsTrigger value="default" data-testid="signature-tab-default">Workspace default</TabsTrigger>
            </TabsList>
            <TabsContent value="mine" className="mt-4"><SignatureForm scope="mine" /></TabsContent>
            <TabsContent value="default" className="mt-4"><SignatureForm scope="default" /></TabsContent>
          </Tabs>
        ) : (
          <div className="mt-4"><SignatureForm scope="mine" /></div>
        )}

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="signature-editor-close">
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
